import React, { useState, useEffect } from 'react';
import { PdfIcon, CloseIcon, ImageIcon } from './icons';
import { useTranslation } from '../i18n';

interface FileListProps {
  files: File[];
  onRemoveFile: (fileName: string) => void;
}

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const FileListItem: React.FC<{ file: File; onRemove: (fileName: string) => void }> = ({ file, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    if (file.type.startsWith('image/')) {
      objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file]);

  const renderFilePreview = () => {
    if (previewUrl) {
      return (
        <img
          src={previewUrl}
          alt={file.name}
          className="h-10 w-10 object-cover rounded-md flex-shrink-0 bg-slate-800"
        />
      );
    }
    if (file.type.startsWith('image/')) {
      return <ImageIcon />; // Fallback icon while image is loading
    }
    if (file.type === 'application/pdf') {
      return <PdfIcon />;
    }
    // Default icon
    return <PdfIcon />;
  };

  return (
    <div className="flex items-center justify-between bg-slate-700/50 p-3 rounded-lg animate-fade-in">
      <div className="flex items-center gap-3 overflow-hidden">
        {renderFilePreview()}
        <div className="flex flex-col overflow-hidden">
          <span className="text-sm font-medium text-slate-200 truncate">{file.name}</span>
          <span className="text-xs text-slate-400">{formatBytes(file.size)}</span>
        </div>
      </div>
      <button
        onClick={() => onRemove(file.name)}
        className="p-1 rounded-full hover:bg-slate-600 transition-colors"
      >
        <CloseIcon />
      </button>
    </div>
  );
};


const FileList: React.FC<FileListProps> = ({ files, onRemoveFile }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-6 space-y-3">
        <h3 className="font-semibold text-slate-300">{t('selectedFiles')}</h3>
      {files.map((file) => (
        <FileListItem 
            key={`${file.name}-${file.lastModified}-${file.size}`} 
            file={file} 
            onRemove={onRemoveFile} 
        />
      ))}
    </div>
  );
};

export default FileList;
